import type {
    DatasetMeta,
    Fip,
    FipDetail,
    Matrix,
    Organization,
    Principle,
    ResourceDetail,
    ResourceFilters,
    ResourceType,
    Stats,
} from '@/types'

export type HealthStatus = {
    status: string
    fips: number
    resources: number
}

async function request<T>(path: string): Promise<T> {
    const response = await fetch(path, { headers: { Accept: 'application/json' } })
    if (!response.ok) {
        throw new Error(`Request to ${path} failed: ${response.status}`)
    }
    return (await response.json()) as T
}

function toQuery(filters: ResourceFilters): string {
    const params = new URLSearchParams()
    if (filters.q?.trim()) {
        params.set('q', filters.q.trim())
    }
    const lists: [string, string[] | undefined][] = [
        ['types', filters.types],
        ['principles', filters.principles],
        ['scopes', filters.scopes],
        ['fips', filters.fips],
        ['providers', filters.providers],
        ['verifiers', filters.verifiers],
    ]
    for (const [key, values] of lists) {
        for (const value of values ?? []) {
            params.append(key, value)
        }
    }
    if (filters.verified !== undefined) {
        params.set('verified', String(filters.verified))
    }
    const query = params.toString()
    return query ? `?${query}` : ''
}

export function fetchHealth(): Promise<HealthStatus> {
    return request<HealthStatus>('/health')
}

export function fetchMeta(): Promise<DatasetMeta> {
    return request<DatasetMeta>('/api/meta')
}

export function fetchPrinciples(): Promise<Principle[]> {
    return request<Principle[]>('/api/principles')
}

export function fetchResourceTypes(): Promise<ResourceType[]> {
    return request<ResourceType[]>('/api/resource-types')
}

export function fetchOrganizations(): Promise<Organization[]> {
    return request<Organization[]>('/api/organizations')
}

export function fetchResources(filters: ResourceFilters = {}): Promise<ResourceDetail[]> {
    return request<ResourceDetail[]>(`/api/resources${toQuery(filters)}`)
}

export function fetchResource(id: string): Promise<ResourceDetail> {
    return request<ResourceDetail>(`/api/resources/${encodeURIComponent(id)}`)
}

export function fetchFips(): Promise<Fip[]> {
    return request<Fip[]>('/api/fips')
}

export function fetchFip(id: string): Promise<FipDetail> {
    return request<FipDetail>(`/api/fips/${encodeURIComponent(id)}`)
}

export function fetchStats(): Promise<Stats> {
    return request<Stats>('/api/stats')
}

export function fetchMatrix(fips: string[] = []): Promise<Matrix> {
    const query = fips.length ? `?fips=${fips.map(encodeURIComponent).join(',')}` : ''
    return request<Matrix>(`/api/matrix${query}`)
}
